/**
 * Manages aria-hidden/inert on body children outside an active trap element.
 * Uses reference counting so overlapping locks restore attributes only when the last lock is released.
 */
type ElementState = {
  lockCount: number;
  previousAriaHidden: string | null;
  wasInert: boolean;
};

export class BackgroundAccessibilityLockService {
  private static readonly backgroundElementStates = new Map<Element, ElementState>();

  public static acquire(trapElement: HTMLElement): () => void {
    const backgroundElements = Array.from(document.body.children).filter((bodyChild) => !bodyChild.contains(trapElement));

    backgroundElements.forEach((backgroundElement) => {
      const state = this.backgroundElementStates.get(backgroundElement);
      if (state) {
        state.lockCount += 1;
        return;
      }

      this.backgroundElementStates.set(backgroundElement, {
        lockCount: 1,
        previousAriaHidden: backgroundElement.getAttribute('aria-hidden'),
        wasInert: backgroundElement.hasAttribute('inert'),
      });

      backgroundElement.setAttribute('aria-hidden', 'true');
      backgroundElement.setAttribute('inert', '');
    });

    let released = false;

    return () => {
      if (released) return;

      released = true;

      backgroundElements.forEach((backgroundElement) => {
        const state = this.backgroundElementStates.get(backgroundElement);
        if (!state) return;

        state.lockCount -= 1;
        if (state.lockCount > 0) return;

        this.backgroundElementStates.delete(backgroundElement);

        if (state.previousAriaHidden === null) {
          backgroundElement.removeAttribute('aria-hidden');
        } else {
          backgroundElement.setAttribute('aria-hidden', state.previousAriaHidden);
        }

        if (!state.wasInert) {
          backgroundElement.removeAttribute('inert');
        }
      });
    };
  }
}
